import _taggedTemplateLiteral from '@babel/runtime/helpers/taggedTemplateLiteral';
import React, { useState as useState$1, useCallback, forwardRef } from 'react';
import { styled } from '@moonpig/launchpad-utils';
import { system, sizingSpacing } from '@moonpig/launchpad-system';
import { Flex, Text, ScreenReaderOnly, IconButton, Box, VerticalStack } from '@moonpig/launchpad-components';
import { IconSystemCheckboxSelected, IconSystemCheckboxIndeterminate, IconSystemCheckbox, IconInformationNote, IconSystemEyeClosed, IconSystemEye, IconSystemRadioButtonSelected, IconSystemRadioButton, IconSystemMinusOutline, IconSystemPlusOutline } from '@moonpig/launchpad-assets';
import { colorValue, spacing, spacingPx } from '@moonpig/launchpad-theme';
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useLocaleText } from '@moonpig/launchpad-localisation';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import styled$1 from 'styled-components';
import { OneTimePasswordField, OneTimePasswordFieldInput, OneTimePasswordFieldHiddenInput } from '@radix-ui/react-one-time-password-field';

var _templateObject$g, _templateObject2$c, _templateObject3$9;
var CHECKBOX_SIZE = '48px';
var INNER_SIZE$1 = '32px';
var getFillColor$1 = function getFillColor(_ref) {
  var checked = _ref.checked,
    indeterminate = _ref.indeterminate,
    disabled = _ref.disabled,
    error = _ref.error;
  if (disabled && indeterminate) {
    return colorValue('colorInteractionDeactivated');
  }
  if (disabled) {
    return colorValue('colorBackgroundDeactivated');
  }
  if (error) {
    if (checked || indeterminate) {
      return colorValue('colorFeedbackError');
    }
    return colorValue('colorBackgroundError');
  }
  if (checked || indeterminate) {
    return colorValue('colorInteractionSelectedState');
  }
  return null;
};
var getHoverFillColor$1 = function getHoverFillColor(_ref2) {
  var checked = _ref2.checked,
    indeterminate = _ref2.indeterminate;
  if (checked || indeterminate) {
    return colorValue('colorInteractionSelectedStateHover');
  }
  return colorValue('colorInteractionControlHoverFill');
};
var getStrokeColor$1 = function getStrokeColor(_ref3) {
  var checked = _ref3.checked,
    indeterminate = _ref3.indeterminate,
    disabled = _ref3.disabled,
    error = _ref3.error;
  if (disabled) {
    return colorValue('colorInteractionDeactivated');
  }
  if (!checked && error) {
    return colorValue('colorFeedbackError');
  }
  if (checked || indeterminate) {
    return null;
  }
  return colorValue('colorInteractionControl');
};
var StyledCheckboxIcon$1 = styled.div(_templateObject$g || (_templateObject$g = _taggedTemplateLiteral(["\n  width: ", ";\n  height: ", ";\n  --stroke-color: ", ";\n  --fill-color: ", ";\n"])), INNER_SIZE$1, INNER_SIZE$1, getStrokeColor$1, function (_ref4) {
  var disabled = _ref4.disabled,
    labelHovered = _ref4.labelHovered;
  return !disabled && labelHovered ? getHoverFillColor$1 : getFillColor$1;
});
var StyledCheckboxInput$1 = styled.input(_templateObject2$c || (_templateObject2$c = _taggedTemplateLiteral(["\n  width: ", ";\n  height: ", ";\n  position: absolute;\n  margin: 0px;\n  cursor: pointer;\n  /* stylelint-disable property-no-vendor-prefix */\n  -webkit-appearance: none;\n  /* stylelint-enable */\n  ", "\n\n  &:disabled {\n    cursor: not-allowed;\n  }\n\n  &:not(:disabled) {\n    &:focus-visible {\n      outline: none;\n      ", "\n    }\n  }\n"])), function (_ref5) {
  var reducedWidth = _ref5.reducedWidth;
  return reducedWidth ? INNER_SIZE$1 : CHECKBOX_SIZE;
}, CHECKBOX_SIZE, system({
  border: 0
}), system({
  boxShadow: 5,
  borderRadius: 1
}));
var StyledCheckbox$1 = styled(Flex)(_templateObject3$9 || (_templateObject3$9 = _taggedTemplateLiteral(["\n  width: ", ";\n  height: ", ";\n  align-items: center;\n  justify-content: center;\n  position: relative;\n\n  &:not([disabled]) {\n    &:hover {\n      ", " {\n        --fill-color: ", ";\n      }\n    }\n  }\n"])), function (_ref6) {
  var reducedWidth = _ref6.reducedWidth;
  return reducedWidth ? INNER_SIZE$1 : CHECKBOX_SIZE;
}, CHECKBOX_SIZE, StyledCheckboxIcon$1, getHoverFillColor$1);
var CheckboxContent = function CheckboxContent(_ref7) {
  var checked = _ref7.checked,
    indeterminate = _ref7.indeterminate,
    disabled = _ref7.disabled,
    error = _ref7.error,
    labelHovered = _ref7.labelHovered,
    dataTestId = _ref7.dataTestId;
  var createIcon = function createIcon() {
    var Icon;
    if (checked) {
      Icon = IconSystemCheckboxSelected;
    } else if (indeterminate) {
      Icon = IconSystemCheckboxIndeterminate;
    } else {
      Icon = IconSystemCheckbox;
    }
    return /*#__PURE__*/React.createElement(Icon, {
      "data-testid": dataTestId
    });
  };
  return /*#__PURE__*/React.createElement(StyledCheckboxIcon$1, {
    "aria-hidden": true,
    checked: checked,
    disabled: disabled,
    indeterminate: indeterminate,
    error: error,
    labelHovered: labelHovered
  }, createIcon());
};
var Checkbox = function Checkbox(_ref8) {
  var name = _ref8.name,
    checked = _ref8.checked,
    onChange = _ref8.onChange,
    value = _ref8.value,
    id = _ref8.id,
    ariaLabel = _ref8.ariaLabel,
    disabled = _ref8.disabled,
    error = _ref8.error,
    required = _ref8.required,
    _ref8$checkedIconTest = _ref8.checkedIconTestId,
    checkedIconTestId = _ref8$checkedIconTest === void 0 ? 'lp-forms-checkbox-checked' : _ref8$checkedIconTest,
    _ref8$uncheckedIconTe = _ref8.uncheckedIconTestId,
    uncheckedIconTestId = _ref8$uncheckedIconTe === void 0 ? 'lp-forms-checkbox-unchecked' : _ref8$uncheckedIconTe,
    _ref8$indeterminateIc = _ref8.indeterminateIconTestId,
    indeterminateIconTestId = _ref8$indeterminateIc === void 0 ? 'lp-forms-checkbox-indeterminate' : _ref8$indeterminateIc,
    reducedWidth = _ref8.reducedWidth,
    indeterminate = _ref8.indeterminate,
    labelHovered = _ref8.labelHovered;
  var checkboxId = id || name;
  var dataTestId;
  if (checked) {
    dataTestId = checkedIconTestId;
  } else if (indeterminate) {
    dataTestId = indeterminateIconTestId;
  } else {
    dataTestId = uncheckedIconTestId;
  }
  return /*#__PURE__*/React.createElement(StyledCheckbox$1, {
    reducedWidth: reducedWidth,
    checked: checked,
    indeterminate: indeterminate,
    disabled: disabled
  }, /*#__PURE__*/React.createElement(CheckboxContent, {
    checked: checked,
    indeterminate: indeterminate,
    disabled: disabled,
    error: error,
    dataTestId: dataTestId,
    labelHovered: labelHovered
  }), /*#__PURE__*/React.createElement(StyledCheckboxInput$1, {
    type: "checkbox",
    name: name,
    checked: checked,
    onChange: onChange,
    value: value,
    id: checkboxId,
    "aria-label": ariaLabel,
    disabled: disabled,
    required: required,
    "aria-required": required,
    reducedWidth: reducedWidth,
    "aria-checked": indeterminate ? 'mixed' : checked
  }));
};

var _excluded$9 = ["name", "children", "label", "id", "disabled", "required", "labelPosition", "inline", "reducedWidth"];
var _templateObject$f, _templateObject2$b;
var COMPONENT_SIZE = '48px';
var StyledFormControlLabel = styled.label(_templateObject$f || (_templateObject$f = _taggedTemplateLiteral(["\n  ", "\n  height: ", ";\n  align-items: center;\n  cursor: ", ";\n\n  ", "\n\n  ", "\n\n  ", "\n\n  ", "\n"])), sizingSpacing, COMPONENT_SIZE, function (_ref) {
  var disabled = _ref.disabled;
  return disabled ? 'not-allowed' : 'pointer';
}, system({
  typography: 'typeBodyText'
}), function (_ref2) {
  var inline = _ref2.inline;
  return {
    display: inline ? 'inline-flex' : 'flex',
    marginInlineEnd: inline ? '16px' : '0'
  };
}, function (_ref3) {
  var labelPosition = _ref3.labelPosition;
  switch (labelPosition) {
    case 'left':
      return "\n          flex-direction: row-reverse;\n          justify-content: flex-end;\n        ";
    case 'left-justified':
      return "\n           flex-direction: row-reverse;\n           justify-content: space-between;\n         ";
    default:
      return "";
  }
}, function (_ref4) {
  var disabled = _ref4.disabled;
  return system({
    color: disabled ? 'colorInteractionDeactivated' : 'colorTextBody'
  });
});
var StyledLabelText = styled.span(_templateObject2$b || (_templateObject2$b = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref5) {
  var labelPosition = _ref5.labelPosition,
    reducedWidth = _ref5.reducedWidth;
  switch (labelPosition) {
    case 'left':
    case 'left-justified':
      return system({
        paddingRight: reducedWidth ? 4 : 0
      });
    default:
      return system({
        paddingLeft: reducedWidth ? 4 : 0
      });
  }
});
var FormControlLabel = function FormControlLabel(_ref6) {
  var name = _ref6.name,
    children = _ref6.children,
    label = _ref6.label,
    id = _ref6.id,
    disabled = _ref6.disabled,
    required = _ref6.required,
    labelPosition = _ref6.labelPosition,
    inline = _ref6.inline,
    _ref6$reducedWidth = _ref6.reducedWidth,
    reducedWidth = _ref6$reducedWidth === void 0 ? false : _ref6$reducedWidth,
    props = _objectWithoutProperties(_ref6, _excluded$9);
  var _useState = useState$1(false),
    _useState2 = _slicedToArray(_useState, 2),
    hovered = _useState2[0],
    setHovered = _useState2[1];
  var inputId = id || name;
  return /*#__PURE__*/React.createElement(StyledFormControlLabel, _extends({
    htmlFor: inputId,
    disabled: disabled,
    required: required,
    labelPosition: labelPosition,
    inline: inline,
    onMouseEnter: function onMouseEnter() {
      return setHovered(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setHovered(false);
    }
  }, props), React.Children.map(children, function (child) {
    // eslint-disable-next-line
    return /*#__PURE__*/React.cloneElement(child, {
      name: name,
      id: inputId,
      required: required,
      disabled: disabled,
      reducedWidth: reducedWidth,
      labelHovered: hovered
    });
  }), /*#__PURE__*/React.createElement(StyledLabelText, {
    labelPosition: labelPosition,
    reducedWidth: reducedWidth
  }, label, required && '\xa0*'));
};

var _templateObject$e;
var StyledLabel$1 = styled.label(_templateObject$e || (_templateObject$e = _taggedTemplateLiteral(["\n  display: block;\n  ", "\n"])), system({
  typography: 'typeBodyLabel',
  color: 'colorTextLabel',
  pb: 3
}));
var Label = function Label(_ref) {
  var children = _ref.children,
    htmlFor = _ref.htmlFor,
    required = _ref.required;
  return /*#__PURE__*/React.createElement(StyledLabel$1, {
    htmlFor: htmlFor
  }, children, required && /*#__PURE__*/React.createElement("span", null, "\u2009*"));
};

var _templateObject$d, _templateObject2$a;
var StyledHelperText = styled(Text)(_templateObject$d || (_templateObject$d = _taggedTemplateLiteral(["\n  display: flex;\n  ", "\n\n  a {\n    ", "\n  }\n\n  &.has-error {\n    ", "\n  }\n"])), system({
  typography: 'typeBodyLabel',
  color: 'colorTextBody',
  pt: 4
}), system({
  color: 'colorTextBody'
}), system({
  color: 'colorFeedbackError'
}));
var StyledIconError = styled(IconInformationNote)(_templateObject2$a || (_templateObject2$a = _taggedTemplateLiteral(["\n  ", "\n  flex-shrink: 0;\n  position: relative;\n  width: 20px;\n  height: 20px;\n"])), system({
  color: 'colorFeedbackError',
  mr: 3,
  my: 'auto'
}));
var HelperText = function HelperText(_ref) {
  var id = _ref.id,
    children = _ref.children,
    error = _ref.error;
  return /*#__PURE__*/React.createElement(StyledHelperText, {
    id: id,
    "aria-live": error ? 'assertive' : 'polite',
    "data-testid": "lp-forms-helperText",
    className: error ? 'has-error' : undefined,
    role: error ? 'alert' : undefined
  }, error && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledIconError, {
    "aria-hidden": true,
    "data-testid": "lp-forms-helperText-error-icon"
  }), ' '), /*#__PURE__*/React.createElement("span", null, children));
};

var INPUT_COMPONENT_HEIGHT = '48px';

var _templateObject$c;
var StyledDescriptorText = styled(Text)(_templateObject$c || (_templateObject$c = _taggedTemplateLiteral(["\n  display: block;\n  ", ";\n"])), system({
  color: 'colorTextBody',
  typography: 'typeBodyCaption',
  pb: 3
}));
var DescriptorText = function DescriptorText(_ref) {
  var id = _ref.id,
    children = _ref.children;
  return /*#__PURE__*/React.createElement(StyledDescriptorText, {
    id: id,
    "data-testid": "lp-forms-descriptorText"
  }, children);
};

var _templateObject$b;
var StyledCharacterLimit = styled.span(_templateObject$b || (_templateObject$b = _taggedTemplateLiteral(["\n  ", "\n  margin-left: auto;\n  flex-shrink: 0;\n"])), system({
  typography: 'typeBodyCaption',
  pt: 3
}));
var CharacterLimit = function CharacterLimit(_ref) {
  var currentLength = _ref.currentLength,
    maxLength = _ref.maxLength,
    isError = _ref.isError;
  var t = useLocaleText();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledCharacterLimit, {
    "data-testid": "mp-character-limit",
    "aria-hidden": true,
    className: isError ? 'has-error' : undefined
  }, currentLength, " / ", maxLength), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, /*#__PURE__*/React.createElement("span", {
    "aria-live": "polite"
  }, t('text_area.characters_limit', currentLength, maxLength))));
};

var _excluded$8 = ["type", "label", "id", "name", "value", "onChange", "onBlur", "onFocus", "placeholder", "descriptor", "disabled", "required", "error", "success", "autoComplete", "autoCorrect", "autoCapitalize", "spellCheck", "helperText", "maxLength", "componentEnd", "componentStart", "componentEndTestId", "componentStartTestId", "hideMaxLengthIndicator", "step"];
var _templateObject$a, _templateObject2$9, _templateObject3$8, _templateObject4, _templateObject5;
var StyledTextInput = styled.div(_templateObject$a || (_templateObject$a = _taggedTemplateLiteral(["\n  ", "\n"])), sizingSpacing);
var StyledInputWrapper = styled.div(_templateObject2$9 || (_templateObject2$9 = _taggedTemplateLiteral(["\n  position: relative;\n"])));
var buildClassName = function buildClassName(error, success) {
  if (error) return 'has-error';
  if (success) return 'has-success';
  return '';
};

/* stylelint-disable property-no-vendor-prefix */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var StyledInput$2 = styled('input').attrs(function (_ref) {
  var error = _ref.error,
    success = _ref.success;
  return {
    className: buildClassName(error, success)
  };
})(_templateObject3$8 || (_templateObject3$8 = _taggedTemplateLiteral(["\n  ", "\n  height: ", ";\n  width: 100%;\n  font: inherit;\n  -webkit-appearance: none;\n\n  ", ";\n\n  ", ";\n\n  &:disabled {\n    ", "\n    cursor: not-allowed;\n\n    &:-internal-autofill-selected,\n    &[data-com-onepassword-filled='light'] {\n      -webkit-box-shadow: 0 0 0px 1000px ", " inset !important;\n    }\n  }\n\n  &:not(:disabled) {\n    &:hover {\n      ", "\n    }\n    &:focus-visible {\n      outline: none;\n      ", "\n    }\n  }\n\n  &::placeholder {\n    ", "\n  }\n\n  &.has-error {\n    ", "\n  }\n\n  &.has-success {\n    ", "\n  }\n"])), system({
  color: 'colorTextHeadline',
  border: 2,
  borderColor: 'colorBlack40',
  borderRadius: 2,
  px: 5,
  pt: 3,
  pb: 4
}), INPUT_COMPONENT_HEIGHT, function (_ref2) {
  var hasComponentEnd = _ref2.hasComponentEnd;
  return hasComponentEnd ? "padding-right: ".concat(INPUT_COMPONENT_HEIGHT) : '';
}, function (_ref3) {
  var hasComponentStart = _ref3.hasComponentStart;
  return hasComponentStart ? "padding-left: ".concat(INPUT_COMPONENT_HEIGHT) : '';
}, system({
  bgcolor: 'colorBackgroundDeactivated',
  borderColor: 'colorInteractionDeactivated',
  color: 'colorInteractionDeactivated'
}), colorValue('colorBlack10'), system({
  borderColor: 'colorBackground07'
}), system({
  boxShadow: 5
}), system({
  color: 'colorBlack60'
}), system({
  bgcolor: 'colorBackgroundError',
  borderColor: 'colorFeedbackError'
}), system({
  bgcolor: 'colorBackgroundSuccess',
  borderColor: 'colorFeedbackSuccess'
}));
/* stylelint-enable */

var StyledComponentEnd = styled.div(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  position: absolute;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  width: ", ";\n  height: ", ";\n"])), INPUT_COMPONENT_HEIGHT, INPUT_COMPONENT_HEIGHT);
var StyledComponentStart = styled.div(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  position: absolute;\n  top: 0;\n  left: 0;\n  bottom: 0;\n  width: ", ";\n  height: ", ";\n"])), INPUT_COMPONENT_HEIGHT, INPUT_COMPONENT_HEIGHT);
var TextInput = /*#__PURE__*/React.forwardRef(function (_ref4, forwardedRef) {
  var _ref4$type = _ref4.type,
    type = _ref4$type === void 0 ? 'text' : _ref4$type,
    label = _ref4.label,
    id = _ref4.id,
    name = _ref4.name,
    value = _ref4.value,
    onChange = _ref4.onChange,
    onBlur = _ref4.onBlur,
    onFocus = _ref4.onFocus,
    placeholder = _ref4.placeholder,
    descriptor = _ref4.descriptor,
    disabled = _ref4.disabled,
    required = _ref4.required,
    error = _ref4.error,
    success = _ref4.success,
    autoComplete = _ref4.autoComplete,
    autoCorrect = _ref4.autoCorrect,
    autoCapitalize = _ref4.autoCapitalize,
    spellCheck = _ref4.spellCheck,
    helperText = _ref4.helperText,
    maxLength = _ref4.maxLength,
    componentEnd = _ref4.componentEnd,
    componentStart = _ref4.componentStart,
    _ref4$componentEndTes = _ref4.componentEndTestId,
    componentEndTestId = _ref4$componentEndTes === void 0 ? 'lp-forms-input-component-end' : _ref4$componentEndTes,
    _ref4$componentStartT = _ref4.componentStartTestId,
    componentStartTestId = _ref4$componentStartT === void 0 ? 'lp-forms-input-component-start' : _ref4$componentStartT,
    hideMaxLengthIndicator = _ref4.hideMaxLengthIndicator,
    _ref4$step = _ref4.step,
    step = _ref4$step === void 0 ? 1 : _ref4$step,
    props = _objectWithoutProperties(_ref4, _excluded$8);
  var inputId = id || name;
  var descriptorId = "".concat(inputId, "__descriptor-text");
  var helperTextId = "".concat(inputId, "__helper-text");
  var disableScroll = function disableScroll(e) {
    // istanbul ignore next
    if (type === 'number') {
      e.target.blur();
    }
    e.preventDefault();
  };
  return /*#__PURE__*/React.createElement(StyledTextInput, props, /*#__PURE__*/React.createElement(Label, {
    htmlFor: inputId,
    required: required,
    disabled: disabled
  }, label), descriptor && /*#__PURE__*/React.createElement(DescriptorText, {
    id: descriptorId
  }, descriptor), /*#__PURE__*/React.createElement(StyledInputWrapper, null, componentStart && /*#__PURE__*/React.createElement(StyledComponentStart, {
    "data-testid": componentStartTestId
  }, componentStart), /*#__PURE__*/React.createElement(StyledInput$2, {
    ref: forwardedRef,
    type: type,
    id: inputId,
    name: name,
    value: value,
    onChange: onChange,
    onBlur: onBlur,
    onFocus: onFocus,
    placeholder: placeholder,
    disabled: disabled,
    required: required,
    autoComplete: autoComplete,
    autoCorrect: autoCorrect,
    autoCapitalize: autoCapitalize,
    spellCheck: spellCheck,
    "aria-required": required,
    "aria-describedby": "".concat(descriptorId, " ").concat(helperTextId),
    "aria-invalid": error,
    error: error,
    success: success,
    maxLength: maxLength,
    hasComponentEnd: !!componentEnd,
    hasComponentStart: !!componentStart,
    step: step,
    onWheel: disableScroll
  }), componentEnd && /*#__PURE__*/React.createElement(StyledComponentEnd, {
    "data-testid": componentEndTestId
  }, componentEnd), /*#__PURE__*/React.createElement(Flex, null, helperText && /*#__PURE__*/React.createElement(HelperText, {
    id: helperTextId,
    error: error
  }, helperText), maxLength && !hideMaxLengthIndicator && /*#__PURE__*/React.createElement(CharacterLimit, {
    currentLength: value.length,
    maxLength: maxLength,
    isError: Boolean(error)
  }))));
});
TextInput.displayName = 'TextInput';

var useState = React.useState;
var PasswordInput = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isVisible = _useState2[0],
    setIsVisible = _useState2[1];
  var t = useLocaleText();
  var componentEnd = /*#__PURE__*/React.createElement(IconButton, {
    icon: isVisible ? IconSystemEyeClosed : IconSystemEye,
    label: isVisible ? t('password_input.hide_password') : t('password_input.show_password'),
    onClick: function onClick() {
      return setIsVisible(!isVisible);
    },
    type: "button",
    "aria-live": "assertive"
  });
  return /*#__PURE__*/React.createElement(TextInput, _extends({
    ref: forwardedRef
  }, props, {
    type: isVisible ? 'text' : 'password',
    componentEnd: componentEnd
  }));
});
PasswordInput.displayName = 'PasswordInput';

var _templateObject$9, _templateObject2$8, _templateObject3$7;
var RADIO_BUTTON_SIZE = '48px';
var INNER_SIZE = '32px';
var getFillColor = function getFillColor(_ref) {
  var checked = _ref.checked,
    disabled = _ref.disabled,
    error = _ref.error;
  if (disabled) {
    return colorValue('colorBackgroundDeactivated');
  }
  if (!checked && error) {
    return colorValue('colorBackgroundError');
  }
  if (checked && error) {
    return colorValue('colorFeedbackError');
  }
  if (checked) {
    return colorValue('colorInteractionSelectedState');
  }
  return null;
};
var getHoverFillColor = function getHoverFillColor(_ref2) {
  var checked = _ref2.checked;
  if (checked) {
    return colorValue('colorInteractionSelectedStateHover');
  }
  return colorValue('colorInteractionControlHoverFill');
};
var getStrokeColor = function getStrokeColor(_ref3) {
  var checked = _ref3.checked,
    disabled = _ref3.disabled,
    error = _ref3.error;
  if (disabled) {
    return colorValue('colorInteractionDeactivated');
  }
  if (!checked && error) {
    return colorValue('colorFeedbackError');
  }
  if (checked && error) {
    return colorValue('colorFeedbackError');
  }
  if (checked) {
    return colorValue('colorInteractionSelectedState');
  }
  return colorValue('colorInteractionControl');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
var StyledRadioButtonIcon = styled.div(_templateObject$9 || (_templateObject$9 = _taggedTemplateLiteral(["\n  width: ", ";\n  height: ", ";\n  --stroke-color: ", ";\n  --fill-color: ", ";\n"])), INNER_SIZE, INNER_SIZE, getStrokeColor, function (_ref4) {
  var disabled = _ref4.disabled,
    labelHovered = _ref4.labelHovered;
  return !disabled && labelHovered ? getHoverFillColor : getFillColor;
});
var StyledRadioButtonInput = styled.input(_templateObject2$8 || (_templateObject2$8 = _taggedTemplateLiteral(["\n  width: ", ";\n  height: ", ";\n  position: absolute;\n  margin: 0px;\n  /* stylelint-disable property-no-vendor-prefix */\n  -webkit-appearance: none;\n  /* stylelint-enable */\n  cursor: pointer;\n  background: transparent;\n  ", "\n\n  &:disabled {\n    cursor: not-allowed;\n  }\n\n  &:not(:disabled) {\n    &:focus-visible {\n      outline: none;\n      ", "\n    }\n  }\n"])), function (_ref5) {
  var reducedWidth = _ref5.reducedWidth;
  return reducedWidth ? INNER_SIZE : RADIO_BUTTON_SIZE;
}, RADIO_BUTTON_SIZE, system({
  border: 0
}), system({
  boxShadow: 5,
  borderRadius: 1
}));
var StyledRadioButton = styled(Flex)(_templateObject3$7 || (_templateObject3$7 = _taggedTemplateLiteral(["\n  width: ", ";\n  height: ", ";\n  align-items: center;\n  justify-content: center;\n\n  &:not([disabled]) {\n    &:hover {\n      ", " {\n        --fill-color: ", ";\n      }\n    }\n  }\n"])), function (_ref6) {
  var reducedWidth = _ref6.reducedWidth;
  return reducedWidth ? INNER_SIZE : RADIO_BUTTON_SIZE;
}, RADIO_BUTTON_SIZE, StyledRadioButtonIcon, getHoverFillColor);
var RadioButtonContent = function RadioButtonContent(_ref7) {
  var checked = _ref7.checked,
    disabled = _ref7.disabled,
    error = _ref7.error,
    labelHovered = _ref7.labelHovered,
    dataTestId = _ref7.dataTestId;
  return /*#__PURE__*/React.createElement(StyledRadioButtonIcon, {
    "aria-hidden": true,
    checked: checked,
    disabled: disabled,
    error: error,
    labelHovered: labelHovered
  }, checked ? /*#__PURE__*/React.createElement(IconSystemRadioButtonSelected, {
    "data-testid": dataTestId
  }) : /*#__PURE__*/React.createElement(IconSystemRadioButton, {
    "data-testid": dataTestId
  }));
};
var RadioButton = function RadioButton(_ref8) {
  var name = _ref8.name,
    checked = _ref8.checked,
    onChange = _ref8.onChange,
    value = _ref8.value,
    id = _ref8.id,
    ariaLabel = _ref8.ariaLabel,
    disabled = _ref8.disabled,
    error = _ref8.error,
    required = _ref8.required,
    _ref8$checkedIconTest = _ref8.checkedIconTestId,
    checkedIconTestId = _ref8$checkedIconTest === void 0 ? 'lp-forms-radio-button-checked' : _ref8$checkedIconTest,
    _ref8$uncheckedIconTe = _ref8.uncheckedIconTestId,
    uncheckedIconTestId = _ref8$uncheckedIconTe === void 0 ? 'lp-forms-radio-button-unchecked' : _ref8$uncheckedIconTe,
    reducedWidth = _ref8.reducedWidth,
    labelHovered = _ref8.labelHovered;
  var radioId = id || name;
  return /*#__PURE__*/React.createElement(StyledRadioButton, {
    reducedWidth: reducedWidth,
    checked: checked,
    disabled: disabled
  }, /*#__PURE__*/React.createElement(RadioButtonContent, {
    checked: checked,
    disabled: disabled,
    error: error,
    labelHovered: labelHovered,
    dataTestId: checked ? checkedIconTestId : uncheckedIconTestId
  }), /*#__PURE__*/React.createElement(StyledRadioButtonInput, {
    type: "radio",
    name: name,
    checked: checked,
    onChange: onChange,
    value: value,
    id: radioId,
    "aria-label": ariaLabel,
    disabled: disabled,
    required: required,
    "aria-required": required,
    reducedWidth: reducedWidth
  }));
};

var _excluded$7 = ["name", "legend", "children", "required", "inline"];
var _templateObject$8, _templateObject2$7, _templateObject3$6;
var StyledRadioGroup = styled.div(_templateObject$8 || (_templateObject$8 = _taggedTemplateLiteral(["\n  ", "\n"])), sizingSpacing);
var StyledLegend$1 = styled.legend(_templateObject2$7 || (_templateObject2$7 = _taggedTemplateLiteral(["\n  ", "\n  display: block;\n"])), system({
  pb: 3,
  typography: 'typeBodyLabel',
  m: 0
}));
var StyledFieldset$1 = styled.fieldset(_templateObject3$6 || (_templateObject3$6 = _taggedTemplateLiteral(["\n  ", "\n"])), system({
  p: 0,
  m: 0,
  border: 0
}));
var RadioGroup = function RadioGroup(_ref) {
  var name = _ref.name,
    legend = _ref.legend,
    children = _ref.children,
    required = _ref.required,
    inline = _ref.inline,
    props = _objectWithoutProperties(_ref, _excluded$7);
  return /*#__PURE__*/React.createElement(StyledRadioGroup, props, /*#__PURE__*/React.createElement(StyledFieldset$1, {
    role: "radiogroup",
    "aria-required": !!required
  }, /*#__PURE__*/React.createElement(StyledLegend$1, null, "".concat(legend, " ").concat(required ? '*' : '')), React.Children.map(children, function (child) {
    return /*#__PURE__*/React.cloneElement(child, {
      name: name,
      inline: inline
    });
  })));
};

var _excluded$6 = ["label", "id", "name", "value", "onChange", "options", "placeholder", "enablePlaceholder", "disabled", "required", "helperText", "descriptorText", "error", "testId"];
var _templateObject$7, _templateObject2$6;
var StyledDropdownWrapper = styled.div(_templateObject$7 || (_templateObject$7 = _taggedTemplateLiteral(["\n  ", "\n"])), sizingSpacing);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
var StyledDropdown = styled('select').attrs(function (_ref) {
  var error = _ref.error;
  return {
    className: error && 'has-error'
  };
})(_templateObject2$6 || (_templateObject2$6 = _taggedTemplateLiteral(["\n  ", "\n  width: 100%;\n  height: ", ";\n  /* stylelint-disable property-no-vendor-prefix */\n  -webkit-appearance: none;\n  /* stylelint-enable */\n  background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCI+CiAgPHBhdGggZmlsbD0idHJhbnNwYXJlbnQiIHN0cm9rZT0iY3VycmVudENvbG9yIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIHN0cm9rZS13aWR0aD0iMiIgZD0ibTYgOSA2IDYgNi02Ii8+Cjwvc3ZnPgo=')\n    no-repeat scroll calc(100% - 12px) 50%/28px ", ";\n  font: inherit;\n  cursor: pointer;\n  ", "\n\n  &:disabled {\n    ", "\n    cursor: not-allowed;\n  }\n\n  &:not(:disabled) {\n    &:hover {\n      ", "\n    }\n    &:focus-visible {\n      outline: none;\n      ", "\n    }\n  }\n\n  &.has-error {\n    ", "\n  }\n"])), system({
  pl: 5,
  pr: 12,
  color: 'colorTextHeadline',
  borderRadius: 2,
  typography: 'typeBodyText'
}), INPUT_COMPONENT_HEIGHT, colorValue('colorBlack00'), system({
  border: 2,
  borderColor: 'colorBlack40'
}), system({
  bgcolor: 'colorBlack10',
  borderColor: 'colorInteractionDeactivated'
}), system({
  borderColor: 'colorBackground07'
}), system({
  boxShadow: 5
}), system({
  bgcolor: 'colorBackgroundError',
  borderColor: 'colorFeedbackError'
}));
var Dropdown = /*#__PURE__*/React.forwardRef(function (_ref2, forwardedRef) {
  var label = _ref2.label,
    id = _ref2.id,
    name = _ref2.name,
    value = _ref2.value,
    onChange = _ref2.onChange,
    options = _ref2.options,
    placeholder = _ref2.placeholder,
    _ref2$enablePlacehold = _ref2.enablePlaceholder,
    enablePlaceholder = _ref2$enablePlacehold === void 0 ? false : _ref2$enablePlacehold,
    disabled = _ref2.disabled,
    required = _ref2.required,
    helperText = _ref2.helperText,
    descriptorText = _ref2.descriptorText,
    error = _ref2.error,
    _ref2$testId = _ref2.testId,
    testId = _ref2$testId === void 0 ? 'lp-forms-dropdown' : _ref2$testId,
    props = _objectWithoutProperties(_ref2, _excluded$6);
  var dropdownId = id || name;
  var descriptorId = "".concat(dropdownId, "__descriptor-text");
  var helperTextId = "".concat(dropdownId, "__helper-text");
  return /*#__PURE__*/React.createElement(StyledDropdownWrapper, _extends({
    "data-testid": testId
  }, props), /*#__PURE__*/React.createElement(Label, {
    htmlFor: dropdownId,
    required: required,
    disabled: disabled
  }, label), descriptorText && /*#__PURE__*/React.createElement(DescriptorText, {
    id: descriptorId
  }, descriptorText), /*#__PURE__*/React.createElement(StyledDropdown, {
    ref: forwardedRef,
    id: dropdownId,
    name: name,
    value: value,
    onChange: onChange,
    disabled: disabled,
    required: required,
    "aria-required": required,
    "aria-describedby": helperTextId,
    "aria-invalid": error,
    error: error
  }, placeholder && /*#__PURE__*/React.createElement("option", {
    value: "",
    disabled: !enablePlaceholder
  }, placeholder), options.map(function (option) {
    return /*#__PURE__*/React.createElement("option", {
      key: "".concat(dropdownId, "-").concat(option.label),
      value: option.value
    }, option.label);
  })), helperText && /*#__PURE__*/React.createElement(HelperText, {
    id: helperTextId,
    error: error
  }, helperText));
});
Dropdown.displayName = 'Dropdown';

var _excluded$5 = ["label", "id", "name", "value", "onChange", "onFocus", "onBlur", "placeholder", "descriptor", "disabled", "required", "error", "autoComplete", "helperText", "maxLength", "rows", "hideMaxLengthIndicator"];
var _templateObject$6, _templateObject2$5, _templateObject3$5;
var SpacedTextArea = styled.div(_templateObject$6 || (_templateObject$6 = _taggedTemplateLiteral(["\n  ", "\n"])), sizingSpacing);
var StyledTextAreaWrapper = styled.div(_templateObject2$5 || (_templateObject2$5 = _taggedTemplateLiteral(["\n  position: relative;\n"])));
var StyledTextArea = styled.textarea(_templateObject3$5 || (_templateObject3$5 = _taggedTemplateLiteral(["\n  ", "\n  width: 100%;\n  font: inherit;\n  /* stylelint-disable property-no-vendor-prefix */\n  -webkit-appearance: none;\n  /* stylelint-enable */\n\n  &:disabled {\n    ", "\n    cursor: not-allowed;\n  }\n\n  &:not(:disabled) {\n    &:hover {\n      ", "\n    }\n    &:focus-visible {\n      outline: none;\n      ", "\n    }\n  }\n\n  &::placeholder {\n    ", "\n  }\n\n  &.has-error {\n    ", "\n  }\n"])), system({
  color: 'colorTextBody',
  border: 2,
  borderColor: 'colorBlack40',
  borderRadius: 2,
  p: 5,
  mb: -3
}), system({
  bgcolor: 'colorBlack10',
  borderColor: 'colorInteractionDeactivated'
}), system({
  borderColor: 'colorBackground07'
}), system({
  boxShadow: 5
}), system({
  color: 'colorInteractionDeactivated'
}), system({
  bgcolor: 'colorBackgroundError',
  borderColor: 'colorFeedbackError'
}));
var TextArea = /*#__PURE__*/React.forwardRef(function (_ref, forwardedRef) {
  var label = _ref.label,
    id = _ref.id,
    name = _ref.name,
    value = _ref.value,
    onChange = _ref.onChange,
    onFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    placeholder = _ref.placeholder,
    descriptor = _ref.descriptor,
    disabled = _ref.disabled,
    required = _ref.required,
    error = _ref.error,
    autoComplete = _ref.autoComplete,
    helperText = _ref.helperText,
    maxLength = _ref.maxLength,
    rows = _ref.rows,
    hideMaxLengthIndicator = _ref.hideMaxLengthIndicator,
    props = _objectWithoutProperties(_ref, _excluded$5);
  var inputId = id || name;
  var descriptorId = "".concat(inputId, "__descriptor-text");
  var helperTextId = "".concat(inputId, "__helper-text");
  return /*#__PURE__*/React.createElement(SpacedTextArea, props, /*#__PURE__*/React.createElement(Label, {
    htmlFor: inputId,
    required: required,
    disabled: disabled
  }, label), descriptor && /*#__PURE__*/React.createElement(DescriptorText, {
    id: descriptorId
  }, descriptor), /*#__PURE__*/React.createElement(StyledTextAreaWrapper, null, /*#__PURE__*/React.createElement(StyledTextArea, {
    className: error ? 'has-error' : undefined,
    ref: forwardedRef,
    id: inputId,
    name: name,
    value: value,
    onChange: onChange,
    onFocus: onFocus,
    onBlur: onBlur,
    placeholder: placeholder,
    disabled: disabled,
    required: required,
    autoComplete: autoComplete,
    "aria-required": required,
    "aria-describedby": helperTextId,
    "aria-invalid": error,
    maxLength: maxLength,
    rows: rows
  }), /*#__PURE__*/React.createElement(Flex, null, helperText && /*#__PURE__*/React.createElement(HelperText, {
    id: helperTextId,
    error: error
  }, helperText), maxLength && !hideMaxLengthIndicator && /*#__PURE__*/React.createElement(CharacterLimit, {
    currentLength: value.length,
    maxLength: maxLength,
    isError: Boolean(error)
  }))));
});
TextArea.displayName = 'TextArea';

var _excluded$4 = ["onChange", "min", "max", "value", "ariaLabel", "id"];
var _templateObject$5, _templateObject2$4, _templateObject3$4;
var DEFAULT_MINIMUM = 1;
var DEFAULT_MAXIMUM = 100;
var StyledInput$1 = styled.input(_templateObject$5 || (_templateObject$5 = _taggedTemplateLiteral(["\n  order: 1;\n  width: 50px;\n  text-align: center;\n  font: inherit;\n  ", ";\n  &:focus {\n    ", ";\n  }\n  &::-webkit-outer-spin-button,\n  &::-webkit-inner-spin-button {\n    appearance: none;\n    margin: 0;\n  }\n\n  appearance: textfield;\n"])), system({
  borderColor: 'colorBorder03'
}), system({
  borderBottom: 4,
  borderColor: 'colorInteractionFocusState'
}));
var SubtractIconButton = styled(IconButton)(_templateObject2$4 || (_templateObject2$4 = _taggedTemplateLiteral(["\n  order: 0;\n"])));
var AddIconButton = styled(IconButton)(_templateObject3$4 || (_templateObject3$4 = _taggedTemplateLiteral(["\n  order: 2;\n"])));
/**
 * @deprecated Please do not use this component in new code. It will be removed in a future release. If you require a quantity input component, please contact the Launchpad team.
 */
var QuantityInput = function QuantityInput(_ref) {
  var onChange = _ref.onChange,
    _ref$min = _ref.min,
    min = _ref$min === void 0 ? DEFAULT_MINIMUM : _ref$min,
    _ref$max = _ref.max,
    max = _ref$max === void 0 ? DEFAULT_MAXIMUM : _ref$max,
    value = _ref.value,
    ariaLabel = _ref.ariaLabel,
    id = _ref.id,
    rest = _objectWithoutProperties(_ref, _excluded$4);
  var modifyBy = useCallback(function (amount) {
    var newValue = value + amount;
    onChange(newValue);
  }, [onChange, value]);
  var inputChanged = useCallback(function (e) {
    var newValue = Number(e.target.value);
    if (newValue !== value && newValue <= max && newValue >= min) {
      onChange(newValue);
    }
  }, [max, min, onChange, value]);
  var handleFocus = function handleFocus(e) {
    e.target.select();
  };
  var t = useLocaleText();
  return /*#__PURE__*/React.createElement(Flex, rest, /*#__PURE__*/React.createElement(SubtractIconButton, {
    icon: IconSystemMinusOutline,
    label: t('quantity_input.subtract_item'),
    onClick: function onClick() {
      return modifyBy(-1);
    },
    disabled: value <= min
  }), /*#__PURE__*/React.createElement(AddIconButton, {
    icon: IconSystemPlusOutline,
    label: t('quantity_input.add_item'),
    onClick: function onClick() {
      return modifyBy(1);
    },
    disabled: value >= max
  }), /*#__PURE__*/React.createElement(StyledInput$1, {
    id: id,
    "aria-label": ariaLabel,
    value: value,
    type: "number",
    onChange: inputChanged,
    onFocus: handleFocus
  }));
};

var _excluded$3 = ["name", "checked", "id", "ariaLabel", "required", "disabled", "labelHovered", "testId"];
var _templateObject$4, _templateObject2$3, _templateObject3$3;
var TOGGLE_HEIGHT = 32;
var TOGGLE_WIDTH = 64;
var TOGGLE_MARGIN = 10;
var getTogglePaddingValue = function getTogglePaddingValue(_ref) {
  var theme = _ref.theme;
  return spacing(3)({
    theme: theme
  });
};
var getToggleHandleRadiusValue = function getToggleHandleRadiusValue(_ref2) {
  var theme = _ref2.theme;
  return TOGGLE_HEIGHT / 2 - getTogglePaddingValue({
    theme: theme
  });
};
var getToggleCheckedOffsetValue = function getToggleCheckedOffsetValue(_ref3) {
  var theme = _ref3.theme;
  return TOGGLE_WIDTH - getToggleHandleRadiusValue({
    theme: theme
  }) * 2 - getTogglePaddingValue({
    theme: theme
  }) * 2;
};
var getBackgroundColor = function getBackgroundColor(_ref4) {
  var isChecked = _ref4.isChecked,
    labelHovered = _ref4.labelHovered;
  if (isChecked) {
    if (labelHovered) {
      return colorValue('colorInteractionSelectedStateHover');
    }
    return colorValue('colorInteractionSelectedState');
  }
  if (labelHovered) {
    return colorValue('colorBlack30');
  }
  return colorValue('colorBlack20');
};
var getBackgroundHoverColor = function getBackgroundHoverColor(_ref5) {
  var isChecked = _ref5.isChecked;
  if (isChecked) {
    return colorValue('colorInteractionSelectedStateHover');
  }
  return colorValue('colorBlack30');
};
var StyledCheckboxInput = styled.input(_templateObject$4 || (_templateObject$4 = _taggedTemplateLiteral(["\n  width: ", "px;\n  height: 44px;\n  position: absolute;\n  margin: 0px;\n  /* stylelint-disable property-no-vendor-prefix */\n  -webkit-appearance: none;\n  /* stylelint-enable */\n  cursor: pointer;\n  z-index: 1;\n  ", "\n\n  &:disabled {\n    cursor: not-allowed;\n  }\n\n  &:not(:disabled) {\n    &:focus-visible {\n      outline: none;\n      ", "\n    }\n  }\n"])), TOGGLE_WIDTH, system({
  border: 0
}), system({
  boxShadow: 5,
  borderRadius: 1
}));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
var StyledCheckboxIcon = styled.div(_templateObject2$3 || (_templateObject2$3 = _taggedTemplateLiteral(["\n  display: block;\n  position: relative;\n  cursor: pointer;\n  outline: none;\n  height: ", "px;\n  width: ", "px;\n  padding: ", "px;\n  background-color: ", ";\n  border-radius: ", "px;\n  transition: background-color 4ms 100ms;\n\n  &:after {\n    height: ", "px;\n    width: ", "px;\n    padding: ", "px;\n    background-color: ", ";\n    border-radius: ", "px;\n    transition: transform 400ms;\n    display: block;\n    position: absolute;\n    content: '';\n\n    ", "\n\n    ", "\n\n    &:not([disabled]) {\n      &:hover {\n        --fill-color: ", ";\n      }\n    }\n  }\n\n  ", ":disabled + & {\n    background-color: ", ";\n    &:after {\n      background-color: ", ";\n    }\n  }\n"])), TOGGLE_HEIGHT, TOGGLE_WIDTH, getTogglePaddingValue, getBackgroundColor, TOGGLE_HEIGHT, getToggleHandleRadiusValue, getToggleHandleRadiusValue, getToggleHandleRadiusValue, colorValue('colorBlack00'), getToggleHandleRadiusValue, function (_ref6) {
  var isChecked = _ref6.isChecked,
    theme = _ref6.theme;
  return isChecked ? "transform: translateX(".concat(getToggleCheckedOffsetValue({
    theme: theme
  }), "px);") : "transform: translateX(0px);";
}, system({
  boxShadow: 1
}), getBackgroundColor, /* sc-selector */StyledCheckboxInput, colorValue('colorBackgroundDeactivated'), colorValue('colorInteractionDeactivated'));
var StyledCheckbox = styled(Flex)(_templateObject3$3 || (_templateObject3$3 = _taggedTemplateLiteral(["\n  min-width: ", "px;\n  height: ", "px;\n  align-items: center;\n  justify-content: center;\n  margin: 0 ", "px;\n\n  &:not([disabled]) {\n    &:hover {\n      ", " {\n        background-color: ", ";\n      }\n    }\n  }\n"])), TOGGLE_WIDTH, TOGGLE_HEIGHT, TOGGLE_MARGIN, StyledCheckboxIcon, getBackgroundHoverColor);
var Toggle = /*#__PURE__*/forwardRef(function (_ref7, ref) {
  var name = _ref7.name,
    checked = _ref7.checked,
    id = _ref7.id,
    ariaLabel = _ref7.ariaLabel,
    required = _ref7.required,
    disabled = _ref7.disabled,
    labelHovered = _ref7.labelHovered,
    testId = _ref7.testId,
    rest = _objectWithoutProperties(_ref7, _excluded$3);
  var toggleId = id || name;
  var toggleTestId = testId || 'lp-forms-switch';
  return /*#__PURE__*/React.createElement(StyledCheckbox, {
    isChecked: checked,
    disabled: disabled
  }, /*#__PURE__*/React.createElement(StyledCheckboxInput, _extends({
    ref: ref,
    type: "checkbox",
    role: "switch",
    id: toggleId,
    "aria-checked": checked,
    "aria-label": ariaLabel,
    "aria-required": required,
    required: required,
    disabled: disabled,
    "data-testid": toggleTestId
  }, rest)), /*#__PURE__*/React.createElement(StyledCheckboxIcon, {
    isChecked: checked,
    labelHovered: labelHovered,
    "aria-hidden": true
  }));
});
Toggle.displayName = 'Toggle';

var _excluded$2 = ["id", "type", "checked", "children", "disabled"];
var _templateObject$3, _templateObject2$2, _templateObject3$2;
var StyledInput = styled$1.div(_templateObject$3 || (_templateObject$3 = _taggedTemplateLiteral(["\n  width: 2rem;\n  height: 2rem;\n  flex-shrink: 0;\n"])));
var StyledInputControl = styled$1.input(_templateObject2$2 || (_templateObject2$2 = _taggedTemplateLiteral(["\n  margin: 0;\n  padding: 0;\n  position: absolute;\n  inset: 0;\n  /* stylelint-disable property-no-vendor-prefix */\n  -webkit-appearance: none;\n  /* stylelint-enable */\n  cursor: pointer;\n  background: transparent;\n  ", "\n\n  .lp-selector-disabled & {\n    cursor: not-allowed;\n  }\n\n  &:focus-visible {\n    outline: none;\n  }\n"])), system({
  border: 0
}));
var StyledLabel = styled$1.label(_templateObject3$2 || (_templateObject3$2 = _taggedTemplateLiteral(["\n  position: relative;\n  display: flex;\n  align-items: center;\n  gap: ", ";\n  min-height: 3.25rem;\n\n  ", "\n\n  &.lp-selector-disabled {\n    ", "\n  }\n\n  &:not(.lp-selector-disabled) {\n    &:hover,\n    &:focus-visible {\n      outline: transparent;\n\n      ", "\n    }\n\n    &:hover {\n      ", "\n    }\n\n    &:has(input:focus-visible) {\n      ", "\n    }\n\n    &.lp-selector-checked {\n      ", "\n    }\n  }\n"])), spacingPx(4), system({
  px: 6,
  py: 4,
  bgcolor: 'colorBackground01',
  color: 'colorTextBody',
  border: 2,
  borderColor: 'colorBlack40',
  borderRadius: 1
}), system({
  bgcolor: 'colorBackgroundDeactivated',
  borderColor: 'colorInteractionDeactivated',
  color: 'colorInteractionDeactivated'
}), system({
  bgcolor: 'colorBackground06'
}), system({
  px: '15px',
  py: '7px',
  border: 3,
  borderColor: 'colorInteractionHoverState',
  boxShadow: 2
}), system({
  boxShadow: 5
}), system({
  px: '14px',
  py: '6px',
  bgcolor: 'colorBackgroundSelectedState',
  border: 4,
  borderColor: 'colorInteractionSelectedState'
}));
var Selector = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var id = _ref.id,
    type = _ref.type,
    checked = _ref.checked,
    children = _ref.children,
    disabled = _ref.disabled,
    rest = _objectWithoutProperties(_ref, _excluded$2);
  return /*#__PURE__*/React.createElement(StyledLabel, {
    className: [].concat(_toConsumableArray(checked ? ['lp-selector-checked'] : []), _toConsumableArray(disabled ? ['lp-selector-disabled'] : [])).join(' '),
    htmlFor: id,
    "data-testid": "lp-selector--".concat(id)
  }, type !== 'plain' && /*#__PURE__*/React.createElement(StyledInput, null, /*#__PURE__*/React.createElement(StyledInputControl, _extends({
    ref: ref,
    type: type,
    checked: checked,
    id: id,
    disabled: disabled,
    "data-testid": "lp-selector-input--".concat(id)
  }, rest)), type === 'radio' && /*#__PURE__*/React.createElement(RadioButtonContent, {
    dataTestId: checked ? "lp-radio-icon--".concat(id, "-checked") : "lp-radio-icon--".concat(id),
    checked: checked,
    disabled: disabled
  }), type === 'checkbox' && /*#__PURE__*/React.createElement(CheckboxContent, {
    dataTestId: checked ? "lp-checkbox-icon--".concat(id, "-checked") : "lp-checkbox-icon--".concat(id),
    checked: checked,
    disabled: disabled
  })), type === 'plain' && /*#__PURE__*/React.createElement(StyledInputControl, _extends({
    ref: ref,
    type: 'radio',
    checked: checked,
    id: id,
    disabled: disabled,
    "data-testid": "lp-selector-input--".concat(id),
    style: {
      opacity: 0
    }
  }, rest)), /*#__PURE__*/React.createElement("div", {
    style: {
      minWidth: '0',
      flex: 1
    }
  }, children));
});
Selector.displayName = 'Selector';

var _templateObject$2;
var StyledContentSelectorContainer = styled.div(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: ", ";\n"])), spacingPx(6));
var SelectorContent = function SelectorContent(_ref) {
  var children = _ref.children,
    icon = _ref.icon;
  return /*#__PURE__*/React.createElement(StyledContentSelectorContainer, null, /*#__PURE__*/React.createElement(Box, {
    flexGrow: "1"
  }, children), icon && /*#__PURE__*/React.cloneElement(icon, {
    style: {
      width: '2rem',
      height: '2rem',
      flexShrink: 0
    },
    role: 'presentation'
  }));
};
var SelectorContentWithBody = function SelectorContentWithBody(_ref2) {
  var headingText = _ref2.headingText,
    bodyText = _ref2.bodyText,
    icon = _ref2.icon;
  return /*#__PURE__*/React.createElement(SelectorContent, {
    icon: icon
  }, /*#__PURE__*/React.createElement(Text, {
    typography: "typeBodyLabel",
    color: "colorTextHeadline",
    display: "block"
  }, headingText), /*#__PURE__*/React.createElement(Text, {
    typography: "typeBodyText",
    color: "colorTextBody",
    display: "block"
  }, bodyText));
};
var SelectorContentWithLabel = function SelectorContentWithLabel(_ref3) {
  var labelText = _ref3.labelText,
    icon = _ref3.icon,
    headingText = _ref3.headingText,
    bodyText = _ref3.bodyText;
  return /*#__PURE__*/React.createElement(SelectorContent, {
    icon: icon
  }, /*#__PURE__*/React.createElement(Text, {
    typography: "typeBodyLabel",
    color: "colorTextHeadline",
    display: "block"
  }, headingText), /*#__PURE__*/React.createElement(Text, {
    typography: "typeBodyText",
    color: "colorTextBody",
    display: "block"
  }, bodyText), /*#__PURE__*/React.createElement(Text, {
    typography: "typeBodyLabel",
    color: "colorTextBody",
    display: "block"
  }, labelText));
};

var _excluded$1 = ["name", "legend", "children", "required", "legendVisible", "spacing"];
var _templateObject$1, _templateObject2$1, _templateObject3$1;
var StyledSelectorGroup = styled.div(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n  ", "\n"])), sizingSpacing);
var StyledLegend = styled.legend(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref) {
  var legendVisible = _ref.legendVisible;
  return legendVisible ? system({
    pb: 3,
    typography: 'typeBodyLabel',
    m: 0,
    display: 'block'
  }) : "\n          position: absolute;\n          overflow: hidden;\n          clip: rect(0 0 0 0);\n          height: 1px;\n          width: 1px;\n          margin: -1px;\n          padding: 0;\n          border: 0;\n        ";
});
var StyledFieldset = styled.fieldset(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteral(["\n  ", "\n"])), system({
  p: 0,
  m: 0,
  border: 0
}));
var SelectorGroup = function SelectorGroup(_ref2) {
  var name = _ref2.name,
    legend = _ref2.legend,
    children = _ref2.children,
    _ref2$required = _ref2.required,
    required = _ref2$required === void 0 ? false : _ref2$required,
    _ref2$legendVisible = _ref2.legendVisible,
    legendVisible = _ref2$legendVisible === void 0 ? false : _ref2$legendVisible,
    _ref2$spacing = _ref2.spacing,
    spacing = _ref2$spacing === void 0 ? 3 : _ref2$spacing,
    props = _objectWithoutProperties(_ref2, _excluded$1);
  return /*#__PURE__*/React.createElement(StyledSelectorGroup, props, /*#__PURE__*/React.createElement(StyledFieldset, {
    role: "radiogroup",
    "aria-required": required
  }, /*#__PURE__*/React.createElement(StyledLegend, {
    legendVisible: legendVisible
  }, "".concat(legend, " ").concat(required ? '*' : '')), /*#__PURE__*/React.createElement(VerticalStack, {
    spacing: spacing
  }, React.Children.map(children, function (child) {
    return /*#__PURE__*/React.cloneElement(child, {
      name: name
    });
  }))));
};

var _excluded = ["id", "label", "length", "testId", "autoComplete", "autoFocus", "autoSubmit", "defaultValue", "disabled", "form", "name", "onAutoSubmit", "onValueChange", "readOnly", "sanitizeValue", "type", "validationType", "value"];
var _templateObject, _templateObject2, _templateObject3;
var StyledCodeInputWrapper = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  ", "\n"])), sizingSpacing);
var StyledOneTimePasswordField = styled(OneTimePasswordField)(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-wrap: nowrap;\n  justify-content: space-between;\n  gap: 8px;\n"])));
var StyledOneTimePasswordFieldInput = styled(OneTimePasswordFieldInput)(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  ", "\n\n  font: inherit;\n  text-align: center;\n  padding: 0;\n  height: 52px;\n  width: 48px;\n  user-select: none;\n\n  &:read-only {\n    ", "\n    cursor: not-allowed;\n    outline: none;\n  }\n\n  &:disabled {\n    ", "\n    cursor: not-allowed;\n  }\n\n  &::selection {\n    background-color: transparent;\n  }\n\n  &:not(:disabled, :read-only) {\n    &:focus-visible {\n      outline: none;\n      ", "\n    }\n  }\n"])), system({
  color: 'colorTextHeadline',
  border: 2,
  borderColor: 'colorBlack40',
  borderRadius: 2,
  p: 5
}), system({
  bgcolor: 'colorBackgroundDeactivated',
  borderColor: 'colorInteractionDeactivated',
  color: 'colorInteractionDeactivated'
}), system({
  bgcolor: 'colorBackgroundDeactivated',
  borderColor: 'colorInteractionDeactivated',
  color: 'colorInteractionDeactivated'
}), system({
  boxShadow: 5
}));
var CodeInput = /*#__PURE__*/React.forwardRef(function (_ref, forwardedRef) {
  var id = _ref.id,
    label = _ref.label,
    _ref$length = _ref.length,
    length = _ref$length === void 0 ? 6 : _ref$length,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? 'lp-forms-code-input' : _ref$testId,
    autoComplete = _ref.autoComplete,
    autoFocus = _ref.autoFocus,
    autoSubmit = _ref.autoSubmit,
    defaultValue = _ref.defaultValue,
    disabled = _ref.disabled,
    form = _ref.form,
    name = _ref.name,
    onAutoSubmit = _ref.onAutoSubmit,
    onValueChange = _ref.onValueChange,
    readOnly = _ref.readOnly,
    sanitizeValue = _ref.sanitizeValue,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'text' : _ref$type,
    _ref$validationType = _ref.validationType,
    validationType = _ref$validationType === void 0 ? 'numeric' : _ref$validationType,
    value = _ref.value,
    props = _objectWithoutProperties(_ref, _excluded);
  var t = useLocaleText();
  var codeInputId = id || name;
  return /*#__PURE__*/React.createElement(StyledCodeInputWrapper, _extends({
    "data-testid": testId
  }, props), /*#__PURE__*/React.createElement(Label, {
    htmlFor: "".concat(codeInputId, "-0"),
    disabled: disabled
  }, label), /*#__PURE__*/React.createElement(StyledOneTimePasswordField, {
    id: codeInputId,
    ref: forwardedRef,
    autoComplete: autoComplete,
    autoFocus: autoFocus,
    autoSubmit: autoSubmit,
    defaultValue: defaultValue,
    disabled: disabled,
    form: form,
    name: name,
    onAutoSubmit: onAutoSubmit,
    onValueChange: onValueChange,
    readOnly: readOnly,
    sanitizeValue: sanitizeValue,
    type: type,
    validationType: validationType,
    value: value,
    tabIndex: readOnly ? -1 : 0
  }, Array.from({
    length: length
  }, function (_, i) {
    return /*#__PURE__*/React.createElement(StyledOneTimePasswordFieldInput, {
      "aria-label": t('code-input.label', label, i, length),
      key: i,
      id: "".concat(codeInputId, "-").concat(i),
      maxLength: length
    });
  }), /*#__PURE__*/React.createElement(OneTimePasswordFieldHiddenInput, null)));
});
CodeInput.displayName = 'CodeInput';

export { Checkbox, CodeInput, Dropdown, FormControlLabel, HelperText, Label, PasswordInput, QuantityInput, RadioButton, RadioGroup, Selector, SelectorContent, SelectorContentWithBody, SelectorContentWithLabel, SelectorGroup, TextArea, TextInput, Toggle };
